import { useCallback, useEffect, useMemo, useState } from "react";
import { EmailSuggestInput } from "../EmailSuggestInput";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Plus, Loader2, MapPin, X, Trash2, Users, CalendarPlus, Check, CalendarRange, Columns2, Filter, Share2, Printer } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 06:00 – 22:00
const HOUR_PX = 44;

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const fmtHour = (h) => { const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 || 12; return `${hh} ${ap}`; };
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const MINI_DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MiniCalendar = ({ anchor, onPick, onMonth }) => {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  return (
    <div data-testid="calendar-mini">
      <div className="flex items-center justify-between px-1 mb-1">
        <span className="text-[14px] font-bold text-ol-text">{MONTHS[anchor.getMonth()]} {anchor.getFullYear()}</span>
        <span className="flex items-center">
          <button onClick={() => onMonth(-1)} className="p-1 text-ol-sub hover:text-ol-blue rounded" aria-label="Previous month" data-testid="mini-prev"><ChevronUp size={15} /></button>
          <button onClick={() => onMonth(1)} className="p-1 text-ol-sub hover:text-ol-blue rounded" aria-label="Next month" data-testid="mini-next"><ChevronDown size={15} /></button>
        </span>
      </div>
      <div className="grid grid-cols-7">
        {MINI_DAYS.map((d, i) => (<span key={i} className="text-center text-[11px] font-semibold text-ol-sub py-1">{d}</span>))}
        {days.map((d, i) => {
          const inM = d.getMonth() === anchor.getMonth();
          const isT = same(d, today);
          return (
            <button key={i} onClick={() => onPick(d)} className={`text-center text-[12px] py-1 rounded-full transition-colors ${isT ? "bg-ol-blue text-white font-bold" : inM ? "text-ol-text hover:bg-ol-hover" : "text-ol-sub hover:bg-ol-hover"}`} data-testid={`mini-day-${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CalendarView = ({ api, contacts = [], accounts = [], activeEmail = "", onSwitchAccount }) => {
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {date, startTime?, event?}
  const [busy, setBusy] = useState(false);
  const [splitView, setSplitView] = useState(false);
  const [filter, setFilter] = useState("all"); // all | allday | timed | attendees
  const [filterOpen, setFilterOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [shareModal, setShareModal] = useState(null); // { to: "", sending: false }

  // visible window per view
  const windowDays = useMemo(() => {
    if (view === "month") {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const start = new Date(first);
      start.setDate(first.getDate() - first.getDay());
      return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    }
    if (view === "week") {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    }
    if (view === "workweek") {
      const start = startOfWeek(anchor);
      start.setDate(start.getDate() + 1); // Monday
      return Array.from({ length: 5 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    }
    const d = new Date(anchor);
    d.setHours(0, 0, 0, 0);
    return [d];
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const to = new Date(windowDays[windowDays.length - 1]);
      to.setDate(to.getDate() + 1);
      const { data } = await api.get("/mail/calendar/events", {
        params: { from: windowDays[0].toISOString(), to: to.toISOString() },
      });
      setEvents(data.events || []);
    } catch {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }, [api, windowDays]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/mail/extras/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, [api, activeEmail]);

  const filteredEvents = useMemo(() => {
    if (filter === "allday") return events.filter((e) => e.all_day);
    if (filter === "timed") return events.filter((e) => !e.all_day);
    if (filter === "attendees") return events.filter((e) => (e.attendees || []).length > 0);
    if (filter.startsWith("cat:")) { const id = filter.slice(4); return events.filter((e) => e.category === id); }
    return events;
  }, [events, filter]);

  const catColor = (e) => categories.find((c) => c.id === e?.category)?.color || null;

  const eventsFor = (d) => filteredEvents.filter((e) => dayKey(new Date(e.start)) === dayKey(d));
  const isToday = (d) => dayKey(d) === dayKey(new Date());

  const move = (n) => setAnchor((a) => {
    const d = new Date(a);
    if (view === "month") return new Date(d.getFullYear(), d.getMonth() + n, 1);
    d.setDate(d.getDate() + n * (view === "week" || view === "workweek" ? 7 : 1));
    return d;
  });

  const heading = view === "month"
    ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : (view === "week" || view === "workweek")
      ? `${windowDays[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${windowDays[windowDays.length - 1].toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
      : anchor.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const save = async (form) => {
    setBusy(true);
    try {
      if (modal.event?.id) {
        const { data } = await api.put(`/mail/calendar/events/${modal.event.id}`, form);
        toast.success(data.invites_sent ? `Event updated — ${data.invites_sent} invite(s) emailed` : "Event updated");
      } else {
        const { data } = await api.post("/mail/calendar/events", form);
        toast.success(data.invites_sent ? `Event added — ${data.invites_sent} invite(s) emailed` : "Event added");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the event");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!modal?.event?.id) return;
    setBusy(true);
    try {
      await api.delete(`/mail/calendar/events/${modal.event.id}`);
      toast.success(modal.event.attendees?.length ? "Event deleted — cancellation emailed to attendees" : "Event deleted");
      setModal(null);
      load();
    } catch {
      toast.error("Could not delete the event");
    } finally {
      setBusy(false);
    }
  };

  const navBtn = "p-2 text-ol-sub hover:text-ol-blue hover:bg-ol-hover rounded-[4px] transition-colors";

  const visibleByDay = () => windowDays
    .map((d) => ({ d, evs: eventsFor(d).sort((a, b) => new Date(a.start) - new Date(b.start)) }))
    .filter((x) => x.evs.length);

  const printCalendar = () => {
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("Allow pop-ups to print the calendar"); return; }
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const byDay = visibleByDay();
    const rows = byDay.length ? byDay.map(({ d, evs }) =>
      `<h3>${esc(d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }))}</h3><ul>${evs.map((e) =>
        `<li><b>${e.all_day ? "All day" : esc(fmtTime(e.start) + " – " + fmtTime(e.end))}</b> — ${esc(e.title)}${e.location ? ` <i>(${esc(e.location)})</i>` : ""}</li>`).join("")}</ul>`).join("")
      : "<p>No events in this range.</p>";
    w.document.write(`<!doctype html><html><head><title>Calendar — ${esc(heading)}</title>
      <style>body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a18;margin:32px}
      h1{border-bottom:2px solid #0a1526;padding-bottom:10px;font-size:22px}
      h3{margin:18px 0 6px;color:#0a1526;font-size:15px}ul{margin:0 0 8px 18px;padding:0}
      li{margin:4px 0;font-size:14px}</style></head><body>
      <h1>NexSecure Calendar — ${esc(heading)}</h1>${rows}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  const shareCalendar = () => setShareModal({ to: "", sending: false });

  const doShareEmail = async () => {
    const to = (shareModal?.to || "").trim();
    if (!to) { toast.error("Enter a colleague's email address"); return; }
    setShareModal((s) => ({ ...s, sending: true }));
    try {
      const toDate = new Date(windowDays[windowDays.length - 1]);
      toDate.setDate(toDate.getDate() + 1);
      const { data } = await api.post("/mail/calendar/share", {
        to,
        from_date: windowDays[0].toISOString(),
        to_date: toDate.toISOString(),
        label: heading,
      });
      toast.success(`Calendar sent to ${data.to} — ${data.events} event(s) as an .ics attachment`);
      setShareModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not share the calendar");
      setShareModal((s) => ({ ...s, sending: false }));
    }
  };

  return (
    <div className="h-full flex bg-white" style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }} data-testid="calendar-view">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-ol-line overflow-y-auto p-3" data-testid="calendar-sidebar">
        <MiniCalendar anchor={anchor} onPick={(d) => { setAnchor(d); if (view === "month") setView("day"); }} onMonth={(n) => move(n)} />
        <button onClick={() => toast.message("Add calendar — coming soon")} className="flex items-center gap-2 mt-3 px-2 py-2 text-[13px] text-ol-blue hover:bg-ol-hover rounded-[4px]" data-testid="calendar-add">
          <CalendarPlus size={16} /> Add calendar
        </button>
        <div className="mt-1 border-t border-ol-line pt-2 space-y-0.5">
          {accounts.map((a) => {
            const on = a.email === activeEmail;
            return (
              <div key={a.email} data-testid={`calendar-acct-${a.email.replace(/[^a-z0-9]/g, "-")}`}>
                <div className="flex items-center gap-1 px-1 py-1.5 text-[13px] font-semibold text-ol-text">
                  <ChevronDown size={14} className="text-ol-sub shrink-0" />
                  <span className="truncate">{a.email}</span>
                </div>
                <button onClick={() => !on && onSwitchAccount?.(a.email)} className={`flex items-center gap-2 w-full pl-6 pr-2 py-1.5 text-[13px] rounded-[4px] transition-colors ${on ? "text-ol-blue font-medium" : "text-ol-text hover:bg-ol-hover"}`} data-testid={`calendar-acct-cal-${a.email.replace(/[^a-z0-9]/g, "-")}`}>
                  <span className={`w-4 h-4 rounded-[3px] border flex items-center justify-center shrink-0 ${on ? "bg-ol-blue border-ol-blue" : "border-ol-line"}`}>{on && <Check size={12} className="text-white" />}</span>
                  Calendar
                </button>
                <button className="block pl-6 pr-2 pb-1 text-[12px] text-ol-blue hover:underline" data-testid={`calendar-acct-showall-${a.email.replace(/[^a-z0-9]/g, "-")}`}>Show all</button>
              </div>
            );
          })}
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 border-b border-ol-line flex-wrap">
        <button
          onClick={() => setModal({ date: anchor })}
          className="flex items-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] transition-colors"
          data-testid="calendar-new-event"
        >
          <Plus size={15} /> New event
        </button>
        <div className="flex items-center rounded-[4px] overflow-hidden border border-ol-line ml-1" data-testid="calendar-views">
          {[["day", "Day"], ["workweek", "Work week"], ["week", "Week"], ["month", "Month"]].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`text-[13px] whitespace-nowrap px-3.5 py-2 transition-colors ${view === v ? "bg-ol-blue text-white font-semibold" : "text-ol-text hover:bg-ol-hover"}`}
              data-testid={`calendar-view-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => setSplitView((s) => !s)} className={`flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-[4px] border transition-colors ${splitView ? "bg-ol-blue text-white border-ol-blue font-semibold" : "text-ol-text border-ol-line hover:bg-ol-hover"}`} data-testid="calendar-split" title="Split view — calendar with an agenda list">
          <Columns2 size={15} /> Split view
        </button>
        <div className="relative">
          <button onClick={() => setFilterOpen((o) => !o)} className={`flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-[4px] border transition-colors ${filter !== "all" ? "bg-ol-blue text-white border-ol-blue font-semibold" : "text-ol-text border-ol-line hover:bg-ol-hover"}`} data-testid="calendar-filter">
            <Filter size={15} /> Filter
          </button>
          {filterOpen && (
            <>
              <div data-lenis-prevent className="fixed inset-0 z-20" onClick={() => setFilterOpen(false)} />
              <div className="absolute left-0 top-11 z-30 w-48 border border-ol-line bg-white shadow-lg rounded-[4px] py-1" data-testid="calendar-filter-menu">
                {[["all", "All events"], ["timed", "Timed only"], ["allday", "All-day only"], ["attendees", "With attendees"]].map(([v, label]) => (
                  <button key={v} onClick={() => { setFilter(v); setFilterOpen(false); }} className={`block w-full text-left px-4 py-1.5 text-[13px] hover:bg-ol-hover transition-colors ${filter === v ? "text-ol-blue font-semibold" : "text-ol-text"}`} data-testid={`calendar-filter-${v}`}>{label}</button>
                ))}
                {categories.length > 0 && (
                  <>
                    <div className="border-t border-ol-line my-1" />
                    <p className="px-4 py-1 text-[10px] uppercase tracking-wide text-ol-sub">By category</p>
                    {categories.map((c) => (
                      <button key={c.id} onClick={() => { setFilter(`cat:${c.id}`); setFilterOpen(false); }} className={`flex items-center gap-2 w-full text-left px-4 py-1.5 text-[13px] hover:bg-ol-hover transition-colors ${filter === `cat:${c.id}` ? "font-semibold" : "text-ol-text"}`} data-testid={`calendar-filter-cat-${c.id}`}>
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} /> {c.name}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <button onClick={shareCalendar} className="flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-[4px] border border-ol-line text-ol-text hover:bg-ol-hover transition-colors" data-testid="calendar-share" title="Share this view">
          <Share2 size={15} /> Share
        </button>
        <button onClick={printCalendar} className="flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-[4px] border border-ol-line text-ol-text hover:bg-ol-hover transition-colors" data-testid="calendar-print" title="Print this view">
          <Printer size={15} /> Print
        </button>
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => setAnchor(new Date())} className="text-[13px] text-ol-blue hover:bg-ol-hover font-medium px-3 py-1.5 rounded-[4px] transition-colors" data-testid="calendar-today">Today</button>
          <button onClick={() => move(-1)} className={navBtn} aria-label="Previous" data-testid="calendar-prev"><ChevronLeft size={18} /></button>
          <button onClick={() => move(1)} className={navBtn} aria-label="Next" data-testid="calendar-next"><ChevronRight size={18} /></button>
          <h2 className="text-[20px] font-bold text-ol-text ml-2" data-testid="calendar-title">{heading}</h2>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-ol-sub text-[13px]" data-testid="calendar-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : view === "month" ? (
          <>
            <div className="grid grid-cols-7 border-b-2 border-ol-line sticky top-0 bg-white z-10">
              {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d) => (
                <div key={d} className="px-1.5 sm:px-3 py-2.5 text-[12px] sm:text-[13px] font-bold text-ol-text border-r border-ol-line last:border-r-0 truncate"><span className="sm:hidden">{d.slice(0, 3)}</span><span className="hidden sm:inline">{d}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-7 auto-rows-fr" data-testid="calendar-grid">
              {windowDays.map((d, i) => {
                const dayEvents = eventsFor(d);
                const inMonth = d.getMonth() === anchor.getMonth();
                const today = isToday(d);
                return (
                  <button
                    key={i}
                    onClick={() => setModal({ date: d })}
                    className={`min-h-[92px] sm:min-h-[112px] border-b border-r border-ol-line p-1.5 text-left align-top transition-colors hover:bg-ol-hover ${inMonth ? "bg-white" : "bg-ol-bg text-ol-sub"} ${today ? "ring-2 ring-inset ring-ol-blue" : ""}`}
                    data-testid={`calendar-day-${d.getDate()}`}
                  >
                    <span className={`inline-flex items-center justify-center min-w-7 h-7 px-1.5 text-[14px] rounded-md font-bold ${today ? "bg-ol-blue text-white" : inMonth ? "text-ol-text" : "text-ol-sub"}`}>
                      {d.getDate() === 1 ? `${d.toLocaleDateString("en-IN", { month: "short" })} ${d.getDate()}` : d.getDate()}
                    </span>
                    <span className="block mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map((e) => (
                        <span
                          key={e.id}
                          role="button" tabIndex={0}
                          onClick={(ev) => { ev.stopPropagation(); setModal({ date: d, event: e }); }}
                          className="block truncate text-[11px] font-medium bg-ol-blue text-white rounded-[3px] px-1.5 py-0.5"
                          style={catColor(e) ? { backgroundColor: catColor(e) } : undefined}
                          data-testid={`calendar-event-${e.id}`}
                        >
                          {!e.all_day && fmtTime(e.start)} {e.title}
                        </span>
                      ))}
                      {dayEvents.length > 3 && <span className="block text-[11px] font-medium text-ol-blue px-1">+{dayEvents.length - 3} more</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          /* week / day time-grid views */
          <div className="flex min-w-[640px]" data-testid="calendar-timeline">
            <div className="w-14 shrink-0 pt-10">
              {HOURS.map((h) => (
                <div key={h} className="text-right pr-2 text-[11px] text-ol-sub" style={{ height: HOUR_PX }}>{fmtHour(h)}</div>
              ))}
            </div>
            {windowDays.map((d) => (
              <DayColumn key={dayKey(d)} day={d} events={eventsFor(d)} isToday={isToday(d)} wide={view === "day"} colorFor={catColor}
                onSlot={(time) => setModal({ date: d, startTime: time })}
                onEvent={(e) => setModal({ date: d, event: e })} />
            ))}
          </div>
        )}
      </div>
      {splitView && (
        <aside className="hidden lg:block w-72 shrink-0 border-l border-ol-line overflow-y-auto bg-ol-bg/40" data-testid="calendar-agenda">
          <div className="px-4 py-3 border-b border-ol-line sticky top-0 bg-white z-10">
            <p className="text-[13px] font-bold text-ol-text">Agenda</p>
            <p className="text-[11px] text-ol-sub">{heading}</p>
          </div>
          {visibleByDay().length === 0 ? (
            <p className="px-4 py-6 text-[12px] text-ol-sub">No events in this range.</p>
          ) : (
            <div className="p-3 space-y-4">
              {visibleByDay().map(({ d, evs }) => (
                <div key={dayKey(d)} data-testid={`calendar-agenda-day-${d.getDate()}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-ol-sub mb-1.5">{d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
                  <div className="space-y-1.5">
                    {evs.map((e) => (
                      <button key={e.id} onClick={() => setModal({ date: d, event: e })} className="block w-full text-left border-l-2 border-ol-blue bg-white hover:bg-ol-hover rounded-r-[4px] px-2.5 py-1.5 transition-colors" style={catColor(e) ? { borderLeftColor: catColor(e) } : undefined} data-testid={`calendar-agenda-event-${e.id}`}>
                        <span className="block text-[12px] font-semibold text-ol-text truncate">{e.title}</span>
                        <span className="block text-[11px] text-ol-sub">{e.all_day ? "All day" : `${fmtTime(e.start)} – ${fmtTime(e.end)}`}{e.location ? ` · ${e.location}` : ""}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      )}
      </div>
      </div>{/* end calendar main */}

      {modal && <EventModal modal={modal} busy={busy} contacts={contacts} categories={categories} onSave={save} onDelete={remove} onClose={() => setModal(null)} />}

      {shareModal && (
        <div data-lenis-prevent className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }} data-testid="calendar-share-modal" onClick={() => !shareModal.sending && setShareModal(null)}>
          <div className="w-full max-w-sm bg-white shadow-2xl rounded-lg overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-ol-blue text-white">
              <h3 className="text-[15px] font-semibold flex items-center gap-2"><Share2 size={16} /> Share calendar</h3>
              <button onClick={() => !shareModal.sending && setShareModal(null)} className="text-white/80 hover:text-white hover:bg-white/10 rounded-[4px] p-1 transition-colors" aria-label="Close" data-testid="calendar-share-close"><X size={18} /></button>
            </div>
            <div className="p-6 flex flex-col gap-3">
              <p className="text-[13px] text-ol-sub">Email the events shown in <b className="text-ol-text">{heading}</b> to a colleague as an <b>.ics</b> attachment they can open in any calendar app.</p>
              <EmailSuggestInput
                theme="light"
                type="email"
                autoFocus
                placeholder="colleague@company.com"
                className="w-full bg-white border border-ol-line text-ol-text placeholder:text-ol-sub px-4 py-2.5 text-[13px] rounded-[4px] focus:outline-none focus:border-ol-blue transition-colors"
                value={shareModal.to}
                disabled={shareModal.sending}
                onChange={(ev) => setShareModal((s) => ({ ...s, to: ev.target.value }))}
                onKeyDown={(ev) => { if (ev.key === "Enter") doShareEmail(); }}
                data-testid="calendar-share-email"
              />
              <button onClick={doShareEmail} disabled={shareModal.sending} className="flex items-center justify-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-6 py-2.5 rounded-[4px] transition-colors disabled:opacity-60" data-testid="calendar-share-send">
                {shareModal.sending ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {shareModal.sending ? "Sending…" : "Send calendar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DayColumn = ({ day, events, isToday, wide, colorFor = () => null, onSlot, onEvent }) => {
  const timed = events.filter((e) => !e.all_day);
  const allDay = events.filter((e) => e.all_day);
  return (
    <div className={`flex-1 border-r border-ol-line ${wide ? "" : "min-w-[86px]"}`} data-testid={`calendar-col-${day.getDate()}`}>
      <div className={`h-10 px-2 pt-2 text-center border-b border-ol-line ${isToday ? "bg-ol-sel" : ""}`}>
        <span className={`inline-flex items-center justify-center w-7 h-7 text-[13px] rounded-md font-bold ${isToday ? "bg-ol-blue text-white" : "text-ol-text"}`}>{day.getDate()}</span>
        <span className="ml-1.5 text-[11px] font-semibold text-ol-sub">{DAYS[day.getDay()]}</span>
      </div>
      {allDay.length > 0 && (
        <div className="px-1 py-1 border-b border-ol-line space-y-1">
          {allDay.map((e) => (
            <button key={e.id} onClick={() => onEvent(e)} className="block w-full truncate text-left text-[11px] font-medium bg-ol-blue text-white rounded-[3px] px-1.5 py-0.5" style={colorFor(e) ? { backgroundColor: colorFor(e) } : undefined} data-testid={`calendar-event-${e.id}`}>{e.title}</button>
          ))}
        </div>
      )}
      <div className="relative" style={{ height: HOURS.length * HOUR_PX }}>
        {HOURS.map((h, i) => (
          <button
            key={h}
            onClick={() => onSlot(`${String(h).padStart(2, "0")}:00`)}
            className="absolute left-0 right-0 border-b border-ol-line hover:bg-ol-hover transition-colors"
            style={{ top: i * HOUR_PX, height: HOUR_PX }}
            aria-label={`New event at ${fmtHour(h)}`}
            data-testid={`calendar-slot-${day.getDate()}-${h}`}
          />
        ))}
        {timed.map((e) => {
          const s = new Date(e.start);
          const en = new Date(e.end);
          const startMin = Math.max(0, (s.getHours() - 6) * 60 + s.getMinutes());
          const durMin = Math.max(30, (en - s) / 60000);
          return (
            <button
              key={e.id}
              onClick={() => onEvent(e)}
              className="absolute left-1 right-1 text-left bg-ol-blue hover:bg-ol-bluehover rounded-[4px] px-1.5 py-1 overflow-hidden transition-colors"
              style={{ top: (startMin / 60) * HOUR_PX, height: Math.max(20, (durMin / 60) * HOUR_PX - 2), ...(colorFor(e) ? { backgroundColor: colorFor(e) } : {}) }}
              data-testid={`calendar-event-${e.id}`}
            >
              <span className="block truncate text-[11px] font-semibold text-white">{e.title}</span>
              <span className="block text-[10px] text-white/80">{fmtTime(e.start)} – {fmtTime(e.end)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const EventModal = ({ modal, busy, contacts, categories = [], onSave, onDelete, onClose }) => {
  const e = modal.event;
  const dateStr = (d) => {
    const z = new Date(d);
    return `${z.getFullYear()}-${String(z.getMonth() + 1).padStart(2, "0")}-${String(z.getDate()).padStart(2, "0")}`;
  };
  const timeStr = (d) => {
    const z = new Date(d);
    return `${String(z.getHours()).padStart(2, "0")}:${String(z.getMinutes()).padStart(2, "0")}`;
  };
  const defaultStart = modal.startTime || (e && !e.all_day ? timeStr(e.start) : "10:00");
  const defaultEnd = e && !e.all_day ? timeStr(e.end) : `${String((parseInt(defaultStart.split(":")[0], 10) + 1) % 24).padStart(2, "0")}:${defaultStart.split(":")[1]}`;
  const [title, setTitle] = useState(e?.title || "");
  const [date, setDate] = useState(e ? dateStr(e.start) : dateStr(modal.date));
  const [allDay, setAllDay] = useState(e?.all_day || false);
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [location, setLocation] = useState(e?.location || "");
  const [notes, setNotes] = useState(e?.notes || "");
  const [attendees, setAttendees] = useState((e?.attendees || []).join(", "));
  const [category, setCategory] = useState(e?.category || "");

  const submit = (ev) => {
    ev.preventDefault();
    onSave({
      title: title.trim(),
      start: allDay ? `${date}T00:00:00` : `${date}T${startTime}:00`,
      end: allDay ? `${date}T23:59:59` : `${date}T${endTime}:00`,
      all_day: allDay,
      location,
      notes,
      category,
      attendees: attendees.split(",").map((a) => a.trim()).filter(Boolean),
    });
  };

  const input = "w-full bg-white border border-ol-line text-ol-text placeholder:text-ol-sub px-4 py-2.5 text-[13px] rounded-[4px] focus:outline-none focus:border-ol-blue transition-colors";

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }} data-testid="event-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-white shadow-2xl rounded-lg max-h-[92vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-ol-blue text-white">
          <h3 className="text-[15px] font-semibold">{e ? "Edit event" : "New event"}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 rounded-[4px] p-1 transition-colors" aria-label="Close" data-testid="event-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3 p-6">
          <input required placeholder="Title — e.g. Site visit, client call" className={input} value={title} onChange={(ev) => setTitle(ev.target.value)} data-testid="event-title" />
          <input type="date" required className={input} value={date} onChange={(ev) => setDate(ev.target.value)} data-testid="event-date" />
          <label className="flex items-center gap-2.5 text-[12px] text-ol-sub cursor-pointer select-none">
            <input type="checkbox" checked={allDay} onChange={(ev) => setAllDay(ev.target.checked)} className="accent-ol-blue w-3.5 h-3.5" data-testid="event-allday" />
            All-day event
          </label>
          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[11px] font-semibold text-ol-sub">
                Starts
                <input type="time" className={`${input} mt-1.5`} value={startTime} onChange={(ev) => setStartTime(ev.target.value)} data-testid="event-start" />
              </label>
              <label className="text-[11px] font-semibold text-ol-sub">
                Ends
                <input type="time" className={`${input} mt-1.5`} value={endTime} onChange={(ev) => setEndTime(ev.target.value)} data-testid="event-end" />
              </label>
            </div>
          )}
          <div className="relative">
            <Users size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-ol-sub" />
            <input
              placeholder="Invite attendees — emails, comma separated (optional)"
              className={`${input} pl-11`}
              value={attendees}
              onChange={(ev) => setAttendees(ev.target.value)}
              list="nx-event-contacts"
              data-testid="event-attendees"
            />
            <datalist id="nx-event-contacts">
              {contacts.filter((c) => c.email).map((c) => (
                <option key={c.id} value={c.email}>{c.name || c.email}</option>
              ))}
            </datalist>
          </div>
          <p className="text-[11px] text-ol-sub leading-relaxed">Attendees get an Outlook-style calendar invitation by email when you save — and a cancellation if you delete the event.</p>
          <div className="relative">
            <MapPin size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-ol-sub" />
            <input placeholder="Location (optional)" className={`${input} pl-11`} value={location} onChange={(ev) => setLocation(ev.target.value)} data-testid="event-location" />
          </div>
          <textarea placeholder="Notes (optional)" rows={2} className={`${input} resize-none`} value={notes} onChange={(ev) => setNotes(ev.target.value)} data-testid="event-notes" />
          {categories.length > 0 && (
            <div data-testid="event-category-row">
              <p className="text-[11px] font-semibold text-ol-sub mb-1.5">Category</p>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setCategory("")} className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${!category ? "border-ol-blue text-ol-blue font-semibold" : "border-ol-line text-ol-sub"}`} data-testid="event-cat-none">None</button>
                {categories.map((c) => (
                  <button type="button" key={c.id} onClick={() => setCategory(c.id)} className="flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border transition-colors" style={category === c.id ? { borderColor: c.color, color: c.color, fontWeight: 600 } : { borderColor: "#e5e0d5", color: "#605e57" }} data-testid={`event-cat-${c.id}`}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} /> {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 pt-1">
            {e ? (
              <button type="button" onClick={onDelete} disabled={busy} className="flex items-center gap-2 text-[13px] text-red-500 hover:text-red-600 px-3 py-2 transition-colors" data-testid="event-delete">
                <Trash2 size={14} /> Delete
              </button>
            ) : <span />}
            <button type="submit" disabled={busy} className="flex items-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-8 py-2 rounded-[4px] transition-colors disabled:opacity-60" data-testid="event-save">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
